import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Lottie from 'lottie-react';
import { Checkbox } from '../../../../../../../../../shared/Checkbox';
import styles from './companies.module.css';
import { Input } from '../../../../../../../../../shared/Input';
import { findFavoritesWorkshopsController } from '../../../../../controller';
import { debounce } from '../../../../../../../../../utils/debounce';
import loadingAnimation from '../../../../../../../../../assets/searchLottie.json';
import { WorkshopList, WorkshopOption } from '../../useSendQuotation';
import { normalizeStrings } from '../../../../../../../../../utils/normalizeStrings';
import { Workshop } from '../../../../../../../../../@types/interface';
import { FilterMenu } from '../../../../../../../../../shared/FilterMenu';

interface WorkshopSearchCache {
  [key: string]: WorkshopOption[];
}

interface Props {
  workshopList: WorkshopList[];
  setWorkshopList: Dispatch<SetStateAction<WorkshopList[]>>;
}

export const Companies = ({ setWorkshopList, workshopList }: Props) => {
  const [companyName, setCompanyName] = useState('');
  const [companiesOptions, setCompaniesOptions] = useState<WorkshopOption[]>(
    [],
  );

  const [searchCache, setSearchCache] = useState<WorkshopSearchCache>({});
  const [isDefaultDataLoading, setIsDefaultDataLoading] = useState(false);
  const [isSearchingForWorkshop, setIsSearchingForWorkshop] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [searchType, setSearchType] = useState<'' | 'service'>('');

  const findWorkshops = useCallback(
    async (query: string, type: string) => {
      if (query.trim().length !== 0) setIsSearchingForWorkshop(true);
      if (searchCache[`${query}-${type}`]) {
        setCompaniesOptions(searchCache[`${query}-${type}`]);
      } else {
        const res = await findFavoritesWorkshopsController(query, type);
        if (res) {
          const normalizedOptions = res.map(workshop => ({
            name: workshop.fantasy_name,
            value: workshop.id_workshop,
          }));

          setSearchCache(prevCache => ({
            ...prevCache,
            [`${query}-${type}`]: normalizedOptions,
          }));
          setCompaniesOptions(normalizedOptions);
        }
      }
      setIsSearchingForWorkshop(false);
    },
    [searchCache],
  );

  const findFavoriteWorkshops = useCallback(async () => {
    setIsDefaultDataLoading(true);
    try {
      const res = await findFavoritesWorkshopsController();
      return res;
    } finally {
      setIsDefaultDataLoading(false);
    }
  }, []);

  // merge the previously was checked and the returns from favorite workshops
  const mergeFavoriteWorkshops = useCallback(
    (newWorkshopList: Workshop[], existingWorkshops: WorkshopList[]) => {
      const newWorkshops = newWorkshopList.filter(
        newWorkshop =>
          !existingWorkshops.some(
            existingWorkshop =>
              existingWorkshop.option.value === newWorkshop.id_workshop,
          ),
      );

      setWorkshopList([
        ...existingWorkshops,
        ...newWorkshops.map(newWorkshop => ({
          option: {
            name: newWorkshop.fantasy_name,
            value: newWorkshop.id_workshop,
          },
          selected: false,
        })),
      ]);
    },
    [setWorkshopList],
  );

  const handleFavoriteWorkshops = useCallback(async () => {
    const newWorkshopList = await findFavoriteWorkshops();
    if (newWorkshopList) {
      mergeFavoriteWorkshops(newWorkshopList, workshopList);
    }
  }, [findFavoriteWorkshops, mergeFavoriteWorkshops]);

  useEffect(() => {
    handleFavoriteWorkshops();
  }, [handleFavoriteWorkshops]);

  const debouncedFindWorkShop = debounce(
    () => findWorkshops(normalizeStrings(companyName), searchType),
    500,
  );
  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { value } = e.target;
    setCompanyName(value);
    debouncedFindWorkShop();
  };

  const handleOptionClick = (option: WorkshopOption) => {
    setWorkshopList(previous => {
      const isThisOptionAlreadyExists = previous.some(
        item => item.option.value === option.value,
      );
      if (isThisOptionAlreadyExists) {
        return sortWorkshopList(
          previous.map(item =>
            item.option.value === option.value
              ? { ...item, selected: true }
              : item,
          ),
        );
      }
      return sortWorkshopList([{ option, selected: true }, ...previous]);
    });
  };

  const handleCheckboxChange = (value: string) => {
    setWorkshopList(previous =>
      sortWorkshopList(
        previous.map(option =>
          option.option.value === value
            ? { ...option, selected: !option.selected }
            : option,
        ),
      ),
    );
  };

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setSelectAll(newValue);
    setWorkshopList(previous =>
      previous.map(option => ({ ...option, selected: newValue })),
    );
  };

  const sortWorkshopList = (workshops: WorkshopList[]) => {
    return workshops.sort((a, b) => {
      if (a.selected && !b.selected) return -1;
      if (!a.selected && b.selected) return 1;
      return 0;
    });
  };

  return (
    <div className={styles['companies-container']}>
      <div>
        <Input
          name="companies"
          type="searchList"
          placeholder={
            searchType === ''
              ? `Digite o nome da empresa`
              : `Digite o nome do serviço`
          }
          options={companiesOptions}
          label="Pesquisar: "
          value={companyName}
          handleOptionClick={handleOptionClick}
          handleChange={handleInputChange}
          isLoading={isSearchingForWorkshop}
        />
        <div className={styles.searchOptions}>
          <FilterMenu
            options={[
              {
                label: 'Oficina',
                value: '',
              },
              {
                label: 'Serviço',
                value: 'service',
              },
            ]}
            defaultValue="name"
            handleChange={(selectedValue: string) =>
              setSearchType(selectedValue as '' | 'service')
            }
          />

          {workshopList.length !== 0 && (
            <Checkbox
              isChecked={selectAll}
              name="selectAll"
              theme="green"
              customSize="small"
              handleChange={handleSelectAll}
              label="Selecionar todas"
              style={{
                fontWeight: '600',
                color: '#333',
                marginLeft: 'auto',
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '1rem',
              }}
            />
          )}
        </div>
      </div>

      {isDefaultDataLoading && <Lottie animationData={loadingAnimation} />}
      {!isDefaultDataLoading && (
        <ul className={styles['companies-list']}>
          {workshopList.map(workshop => (
            <li key={workshop.option.value}>
              <Checkbox
                isChecked={workshop.selected}
                name={workshop.option.value}
                theme="green"
                customSize="small"
                handleChange={() => handleCheckboxChange(workshop.option.value)}
                label={workshop.option.name}
                style={{
                  fontWeight: '600',
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
